<template>
  <!-- The row under a parent's hero: the parent first, then its children (US 9369 + 9371).
       Used for a process with its activities and a third party with its contracts. -->
  <nav class="ps" :class="{ 'is-one': children.length === 1 }" :qid="qid">
    <button type="button"
            class="ps__parent"
            :class="{ on: selected === 'parent' }"
            :aria-current="selected === 'parent' ? 'page' : null"
            :qid="qid + '-parent'"
            @click="pick('parent')">
      <span class="ps__icon"><font-awesome-icon :icon="parentIcon"/></span>
      <span class="ps__text">
        <small>{{ parentKind }}</small>
        <b>{{ parent.name }}</b>
        <span>{{ parentSub }}</span>
      </span>
      <span v-if="parent.warn" class="ps__dot is-warn" :title="parentWarnText"></span>
    </button>

    <div class="ps__label">
      <small>{{ childLabel }}</small>
      <b>{{ ownChildren.length ? ownChildren.length : $t('parent_switcher.none') }}<template v-if="sharedChildren.length"> + {{ $tc('parent_switcher.shared_count', sharedChildren.length, { count: sharedChildren.length }) }}</template></b>
    </div>

    <div class="ps__items">
      <button v-for="child in rowChildren"
              :key="child.id"
              type="button"
              class="ps__item"
              :class="{ on: selected === child.id, 'is-shared': child.shared }"
              :aria-current="selected === child.id ? 'page' : null"
              :title="child.shared ? $t('parent_switcher.lives_in', { name: child.lives_in }) : child.name"
              :qid="qid + '-child-' + child.id"
              @click="pick(child)">
        <span class="ps__icon"><font-awesome-icon :icon="child.shared ? 'link' : childIcon"/></span>
        <span class="ps__text">
          <b>{{ child.name }}</b>
          <span>{{ child.shared ? $t('parent_switcher.lives_in', { name: child.lives_in }) : child.sub }}</span>
        </span>
        <span v-if="child.shared" class="ps__shared">{{ $t('parent_switcher.decide') }}</span>
        <span v-else class="ps__dot" :class="child.warn ? 'is-warn' : 'is-ok'" :title="child.warnText"></span>
      </button>

      <div v-if="hiddenChildren.length" ref="moreWrap" class="ps__more-wrap">
        <button type="button"
                class="ps__more"
                :class="{ open: moreOpen }"
                :aria-expanded="moreOpen ? 'true' : 'false'"
                :qid="qid + '-more'"
                @click="toggleMore">
          {{ $t('parent_switcher.more', { count: hiddenChildren.length }) }}
          <span v-if="hiddenChildren.some(child => child.warn)" class="ps__dot is-warn"></span>
          <font-awesome-icon icon="angle-down"/>
        </button>
      </div>

      <button v-if="canAdd"
              type="button"
              class="ps__add"
              :qid="qid + '-add'"
              @click="$emit('add')">
        <font-awesome-icon icon="plus"/>{{ children.length ? addLabel : addFirstLabel }}
      </button>
    </div>

    <!-- Outside the row, so a row that scrolls sideways doesn't clip it -->
    <div v-if="moreOpen" ref="pop" class="ps__pop" :qid="qid + '-more-list'">
      <label class="ps__search">
        <font-awesome-icon icon="search"/>
        <input ref="search"
               v-model="search"
               type="text"
               :placeholder="$t('parent_switcher.search', { count: children.length })"
               :qid="qid + '-search'">
      </label>
      <div class="ps__filters">
        <button type="button" :class="{ on: filter === 'all' }" @click="filter = 'all'">{{ $t('parent_switcher.filter_all', { count: children.length }) }}</button>
        <button type="button" :class="{ on: filter === 'warn' }" @click="filter = 'warn'">{{ $t('parent_switcher.filter_warn', { count: warnCount }) }}</button>
      </div>
      <template v-for="group in popGroups">
        <div v-if="group.items.length" :key="group.key" class="ps__group">{{ group.title }}</div>
        <button v-for="child in group.items"
                :key="group.key + child.id"
                type="button"
                class="ps__row"
                :class="{ on: selected === child.id }"
                @click="pick(child)">
          <span class="ps__icon"><font-awesome-icon :icon="child.shared ? 'link' : childIcon"/></span>
          <span class="ps__text">
            <b>{{ child.name }}</b>
            <span>{{ child.shared ? $t('parent_switcher.lives_in', { name: child.lives_in }) : child.sub }}</span>
          </span>
          <span v-if="selected === child.id" class="ps__picked">{{ $t('parent_switcher.selected') }}</span>
          <span v-if="!child.shared" class="ps__dot" :class="child.warn ? 'is-warn' : 'is-ok'"></span>
        </button>
      </template>
      <p v-if="!popGroups.some(group => group.items.length)" class="ps__empty">{{ $t('parent_switcher.no_match') }}</p>
    </div>

    <div v-if="note" class="ps__note"><font-awesome-icon icon="lock"/>{{ note }}</div>
  </nav>
</template>

<script>
  /**
   * Props:
   * - parent: { name, warn }
   * - children: [{ id, name, sub, warn, warnText, shared, lives_in }], own children first
   * - selected: 'parent' or a child id
   * Emits select (the child, or 'parent') and add.
   */
  export default {
    name: 'V2ParentSwitcher',
    props: {
      parent: { type: Object, required: true },
      children: { type: Array, default: () => [] },
      selected: { type: [String, Number], default: 'parent' },
      parentKind: { type: String, required: true },
      parentSub: { type: String, default: '' },
      parentIcon: { type: String, default: 'sitemap' },
      parentWarnText: { type: String, default: '' },
      childLabel: { type: String, required: true },
      childIcon: { type: String, default: 'cube' },
      addLabel: { type: String, default: '' },
      addFirstLabel: { type: String, default: '' },
      canAdd: { type: Boolean, default: false },
      note: { type: String, default: '' },
      // How many children fit in the row with the parent; the rest go behind "+ n more"
      rowSize: { type: Number, default: 4 },
      qid: { type: String, default: 'parent-switcher' }
    },
    data() {
      return {
        moreOpen: false,
        search: '',
        filter: 'all'
      };
    },
    computed: {
      ownChildren() {
        return this.children.filter(child => !child.shared);
      },
      sharedChildren() {
        return this.children.filter(child => child.shared);
      },
      // With more than rowSize + 1 children, the row keeps rowSize and always the selected one
      rowChildren() {
        if (this.children.length <= this.rowSize + 1) {
          return this.children;
        }
        let row = this.children.slice(0, this.rowSize);
        let picked = this.children.find(child => child.id === this.selected);
        if (picked && row.indexOf(picked) === -1) {
          row = row.slice(0, this.rowSize - 1).concat([picked]);
        }
        return row;
      },
      hiddenChildren() {
        return this.children.filter(child => this.rowChildren.indexOf(child) === -1);
      },
      warnCount() {
        return this.children.filter(child => child.warn).length;
      },
      popGroups() {
        let words = this.search.trim().toLowerCase();
        let keep = child => (!words || child.name.toLowerCase().indexOf(words) !== -1)
          && (this.filter !== 'warn' || child.warn);
        return [
          { key: 'row', title: this.$t('parent_switcher.in_row'), items: this.rowChildren.filter(keep) },
          { key: 'hidden', title: this.$t('parent_switcher.not_in_row', { count: this.hiddenChildren.length }), items: this.hiddenChildren.filter(keep) }
        ];
      }
    },
    methods: {
      pick(child) {
        this.moreOpen = false;
        this.$emit('select', child);
      },
      toggleMore() {
        this.moreOpen = !this.moreOpen;
        if (this.moreOpen) {
          this.search = '';
          this.filter = 'all';
          this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
        }
      },
      // A click outside the list, or Escape, closes it
      outside(event) {
        let inside = [this.$refs.moreWrap, this.$refs.pop].some(el => el && el.contains(event.target));
        if (this.moreOpen && !inside) {
          this.moreOpen = false;
        }
      },
      escape(event) {
        if (event.key === 'Escape') {
          this.moreOpen = false;
        }
      }
    },
    mounted() {
      document.addEventListener('mousedown', this.outside);
      document.addEventListener('keydown', this.escape);
    },
    beforeDestroy() {
      document.removeEventListener('mousedown', this.outside);
      document.removeEventListener('keydown', this.escape);
    }
  };
</script>
